import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";

/** One verified TOTP factor, as returned by mfa.listFactors().totp. */
interface TotpFactor {
  id: string;
  friendlyName?: string;
}

/** An in-progress enrollment: the QR (SVG data URI) + manual secret to display
 * while the user adds the account to their authenticator, plus the factor id the
 * verify step challenges against. */
interface Enrolling {
  factorId: string;
  qr: string;
  secret: string;
}

/**
 * Security section of the Control Center: change password (reset email or an
 * inline update) and manage two-factor authentication (TOTP) via Supabase's
 * native MFA.
 */
export function Security({ email }: { email?: string }) {
  // --- Change password (via emailed reset link) ---
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwNotice, setPwNotice] = useState<string | null>(null);

  // --- Two-factor (TOTP) ---
  // null while the first listFactors() is in flight, then the verified factors.
  const [factors, setFactors] = useState<TotpFactor[] | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaNotice, setMfaNotice] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<Enrolling | null>(null);
  const [code, setCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [confirmingUnenroll, setConfirmingUnenroll] = useState<string | null>(null);

  const loadFactors = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) {
      setMfaError(error.message);
      setFactors([]);
      return;
    }
    setFactors((data.totp ?? []).map((f) => ({ id: f.id, friendlyName: f.friendly_name ?? undefined })));
  }, []);

  useEffect(() => {
    void loadFactors();
  }, [loadFactors]);

  async function sendResetEmail() {
    if (!email) {
      setPwError("No email on file for this account.");
      return;
    }
    setPwBusy(true);
    setPwError(null);
    setPwNotice(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setPwNotice("Check your email for a link to reset your password.");
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Couldn't send a reset email.");
    } finally {
      setPwBusy(false);
    }
  }

  async function startEnroll() {
    setMfaError(null);
    setMfaNotice(null);
    setMfaBusy(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
      if (error) {
        // The most common cause is project-level MFA being disabled. Surface a
        // clear, non-breaking message rather than a raw API error.
        setMfaError(
          /enabled|disabled|not\s+available|mfa/i.test(error.message)
            ? "Two-factor isn't available on this account yet."
            : error.message,
        );
        return;
      }
      setEnrolling({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
      setCode("");
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : "Couldn't start two-factor setup.");
    } finally {
      setMfaBusy(false);
    }
  }

  async function verifyEnroll(e: React.FormEvent) {
    e.preventDefault();
    if (!enrolling) return;
    setMfaBusy(true);
    setMfaError(null);
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: enrolling.factorId,
      });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: enrolling.factorId,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (vErr) throw vErr;
      setEnrolling(null);
      setCode("");
      setMfaNotice("2FA enabled.");
      await loadFactors();
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : "That code didn't match. Try again.");
    } finally {
      setMfaBusy(false);
    }
  }

  // Abandon an in-progress enrollment. The unverified factor is removed so a
  // later attempt starts clean rather than piling up orphaned factors.
  async function cancelEnroll() {
    const factorId = enrolling?.factorId;
    setEnrolling(null);
    setCode("");
    setMfaError(null);
    if (factorId) await supabase.auth.mfa.unenroll({ factorId }).catch(() => {});
  }

  async function unenroll(factorId: string) {
    setConfirmingUnenroll(null);
    setMfaBusy(true);
    setMfaError(null);
    setMfaNotice(null);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      setMfaNotice("Two-factor turned off.");
      await loadFactors();
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : "Couldn't turn off two-factor.");
    } finally {
      setMfaBusy(false);
    }
  }

  const enabled = (factors?.length ?? 0) > 0;

  return (
    <div className="card security">
      <h2>Security</h2>

      {/* Change password ------------------------------------------------ */}
      <div className="security-block">
        <div className="security-block-title">Password</div>
        <p className="muted small">
          We’ll email you a secure link to set a new password.
        </p>
        <button
          type="button"
          className="btn small"
          disabled={pwBusy}
          onClick={() => void sendResetEmail()}
        >
          {pwBusy ? "…" : "Send reset email"}
        </button>
        {pwError && <p className="error small">{pwError}</p>}
        {pwNotice && <p className="notice small">{pwNotice}</p>}
      </div>

      {/* Two-factor authentication ------------------------------------- */}
      <div className="security-block">
        <div className="security-block-title">
          Two-factor authentication
          {factors !== null && (
            <span className={`tag ${enabled ? "on" : ""}`}>{enabled ? "Enabled" : "Disabled"}</span>
          )}
        </div>
        <p className="muted small">
          Add a time-based code from an authenticator app (Google Authenticator, 1Password, Authy)
          as a second step when you sign in.
        </p>

        {factors === null ? (
          <p className="muted small">Loading…</p>
        ) : enrolling ? (
          <form onSubmit={verifyEnroll} className="mfa-enroll">
            <p className="muted small">
              Scan this QR code with your authenticator app, or enter the key manually, then type the
              6-digit code it shows.
            </p>
            <img className="mfa-qr" src={enrolling.qr} alt="Two-factor QR code" />
            <code className="mfa-secret">{enrolling.secret}</code>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <div className="row">
              <button type="submit" className="btn small grow" disabled={mfaBusy || code.trim().length < 6}>
                Verify &amp; enable
              </button>
              <button type="button" className="ghost small grow" disabled={mfaBusy} onClick={() => void cancelEnroll()}>
                Cancel
              </button>
            </div>
          </form>
        ) : enabled ? (
          <ul className="mfa-factors">
            {factors.map((f) => (
              <li key={f.id} className="mfa-factor">
                <span className="active-pill">
                  <span className="active-dot" aria-hidden="true" />
                  {f.friendlyName || "Authenticator app"}
                </span>
                {confirmingUnenroll === f.id ? (
                  <span className="confirm-inline">
                    <button className="ghost small" disabled={mfaBusy} onClick={() => void unenroll(f.id)}>
                      Confirm
                    </button>
                    <button className="ghost small" onClick={() => setConfirmingUnenroll(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button className="ghost small" disabled={mfaBusy} onClick={() => setConfirmingUnenroll(f.id)}>
                    Turn off
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <button type="button" className="btn small" disabled={mfaBusy} onClick={() => void startEnroll()}>
            Enable two-factor
          </button>
        )}

        {mfaError && <p className="error small">{mfaError}</p>}
        {mfaNotice && <p className="notice small">{mfaNotice}</p>}
      </div>
    </div>
  );
}
